/**
 * Unit-scoped availability fetch for the merged table's availability columns
 * (offer-unified-table, CU-86cbc3r8b) — requests availability only for the
 * rows currently on screen (see the offer-unified-table Requirement
 * "Availability is shown as period columns within the merged table").
 *
 * `from`/`to` are OPTIONAL (CU-86cbejhgd): the rendered availability columns
 * caller (`use-offer-availability-query.ts`) omits them entirely so the
 * endpoint returns its backward-compatible default — the offer's own full,
 * unpaginated period range — matching the retired availability tab's
 * `useOfferAvailability`, which always fetched the whole, unfiltered offer.
 * The separate bulk-remove reservation-check caller
 * (`use-offer-reservation-check.ts`) still passes its own capped range, since
 * that fetch has its own reason to stay bounded under the endpoint's max
 * period-count validation.
 */
import { useQuery } from '@tanstack/react-query';
import { offerAvailability } from '@ooh/contracts';
import type { OfferAvailabilityResponse } from '@ooh/contracts';
import { offerAvailabilityKeys, useEffectiveAuth } from '../../availability-shared/api/queries';
import type { WithCallWithAuth } from '../../availability-shared/api/queries';

export interface UseOfferUnitAvailabilityParams {
  /** Offer-unit ids currently loaded/visible in the merged table (at most 200). */
  unitIds: number[];
  /** Period window start (ISO date). Omit (with `to`) to get the offer's full range. */
  from?: string;
  /** Period window end (ISO date). Omit (with `from`) to get the offer's full range. */
  to?: string;
}

export interface UseOfferUnitAvailabilityResult {
  units: OfferAvailabilityResponse['units'];
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useOfferUnitAvailability(
  offerId: number,
  { unitIds, from, to }: UseOfferUnitAvailabilityParams,
  auth?: WithCallWithAuth,
): UseOfferUnitAvailabilityResult {
  const effectiveAuth = useEffectiveAuth(auth);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      ...offerAvailabilityKeys.availability(offerId),
      'unit-scoped',
      [...unitIds].sort((a, b) => a - b).join(','),
      from ?? '',
      to ?? '',
    ],
    queryFn: () =>
      effectiveAuth.callWithAuth(offerAvailability.getAvailability, {
        offerId,
        unitIds,
        ...(from !== undefined && to !== undefined ? { from, to } : {}),
      }),
    enabled: unitIds.length > 0,
    placeholderData: (prev: OfferAvailabilityResponse | undefined) => prev,
    staleTime: 30_000,
  });

  return {
    units: data?.units ?? [],
    defaultDateFrom: data?.defaultDateFrom ?? null,
    defaultDateTo: data?.defaultDateTo ?? null,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
